import {showDeleteConfirmation} from '@modules/common';
import {switchMap, tap, map} from 'rxjs/operators';
import {BaseBloc} from '@core/utils/bloc';
import {Repository} from '@core/utils/repository';
import {handleMessage, requestMessage} from '@business/messages';
import {
    TimeMaterialTemplateRequest,
    TimeMaterialTemplateResponse,
    CompactTimeMaterialTemplateResponse,
    TimeMaterialTemplateId,
    TemplateStepResponse,
} from '@business/entities/tm';
import {TMTemplateAPI} from '@business/api/tm_template';
import {R} from '@core/utils/r';

export class TMTemplateBloc extends BaseBloc {
    private readonly tmTempRepo = new Repository<CompactTimeMaterialTemplateResponse | TimeMaterialTemplateResponse>({
        getItemId: template => template.id,
    });

    readonly tmTemplates$ = this.tmTempRepo.items$.pipe(map(items => R.sortBy(items, i => i.name.toLowerCase())));
    readonly selectTMTemplate = this.tmTempRepo.selectItem;

    readonly selectStep = (tmId: TimeMaterialTemplateId, stepId: number) =>
        this.tmTempRepo.selectItem(tmId).pipe(
            map(order => {
                if (!order) return undefined;
                const allSteps = R.flatMap((order as TimeMaterialTemplateResponse).sections || [], section => section.steps);
                return allSteps.find(s => s.id === stepId);
            }),
        );

    onReset() {
        this.tmTempRepo.reset();
    }

    fetchTMTemplates = () => {
        return TMTemplateAPI.fetchTMTemplates().pipe(
            this.tmTempRepo.ops.reset(),
            handleMessage({error: requestMessage('fetch_tm_templates_error')}),
        );
    };

    fetchTMTemplate = (id: TimeMaterialTemplateId) => {
        return TMTemplateAPI.fetchTMTemplate(id).pipe(
            this.tmTempRepo.ops.upsertOne(item => ({item})),
            handleMessage({error: requestMessage('fetch_tm_templates_error')}),
        );
    };

    createTMTemplate = (order: TimeMaterialTemplateRequest) => {
        return TMTemplateAPI.createTMTemplate(order).pipe(
            this.tmTempRepo.ops.addOne(item => item),
            handleMessage({
                type: requestMessage('create_tm_template'),
            }),
        );
    };

    updateTMTemplate = (id: TimeMaterialTemplateId, order: TimeMaterialTemplateRequest) => {
        return TMTemplateAPI.updateTMTemplate(id, order).pipe(
            this.tmTempRepo.ops.upsertOne(item => ({item})),
            handleMessage({
                type: requestMessage('update_tm_template'),
            }),
        );
    };

    duplicateTMTemplate = (id: TimeMaterialTemplateId, template: TimeMaterialTemplateResponse) => {
        return showDeleteConfirmation('Duplicate Template', 'Do you really want to duplicate this Template?', {btnLabel: 'Duplicate'}).pipe(
            switchMap(() => TMTemplateAPI.duplicateTMTemplate(id, template)),
            this.tmTempRepo.ops.upsertOne(item => ({item})),
            handleMessage({
                type: requestMessage('duplicate_tm_template'),
            }),
        );
    };

    deleteTMTemplate = (id: TimeMaterialTemplateId) => {
        return showDeleteConfirmation('Delete Template', 'Do you really want to remove this template?').pipe(
            switchMap(() => TMTemplateAPI.deleteTMTemplate(id)),
            this.tmTempRepo.ops.removeOne(() => id),
            handleMessage({
                type: requestMessage('delete_tm_template'),
            }),
        );
    };

    /* Sections */
    createTemplateSection = (templateId: TimeMaterialTemplateId, name: string, isPublic = false) => {
        return TMTemplateAPI.createTemplateSection(templateId, name, isPublic).pipe(
            switchMap(() => this.fetchTMTemplate(templateId)),
            handleMessage({
                type: requestMessage('create_tm_template_section'),
            }),
        );
    };

    updateTemplateSection = (templateId: TimeMaterialTemplateId, id: number, name: string, isPublic = false) => {
        return TMTemplateAPI.updateTemplateSection(id, name, isPublic).pipe(
            switchMap(() => this.fetchTMTemplate(templateId)),
            handleMessage({
                type: requestMessage('update_tm_template_section'),
            }),
        );
    };

    deleteTemplateSection = (templateId: TimeMaterialTemplateId, id: number) => {
        return showDeleteConfirmation('Delete Section', 'Do you really want to remove this Section?').pipe(
            switchMap(() => TMTemplateAPI.deleteTemplateSection(id)),
            switchMap(() => this.fetchTMTemplate(templateId)),
            handleMessage({
                type: requestMessage('delete_tm_template_section'),
            }),
        );
    };

    sortSections = (templateId: TimeMaterialTemplateId, sectionIds: number[]) => {
        return TMTemplateAPI.sortSections(sectionIds).pipe(
            tap(() => {
                const template = this.tmTempRepo.getItem(templateId)! as TimeMaterialTemplateResponse;
                const newSections = template.sections.map(sec => {
                    return {...sec, menuOrder: R.findIndex(sectionIds, i => i === sec.id)};
                });

                this.tmTempRepo.updateOne({
                    id: templateId,
                    changes: () => ({sections: R.sortBy(newSections, s => s.menuOrder)}),
                });
            }),
            handleMessage({
                error: requestMessage('update_tm_template_section_sort'),
            }),
        );
    };

    /* Steps */

    fetchStep = (templateId: TimeMaterialTemplateId, id: number) => {
        return TMTemplateAPI.fetchStep(id).pipe(
            handleMessage({
                error: requestMessage('fetch_tm_step'),
            }),
        );
    };

    createTemplateSectionStep = (templateId: TimeMaterialTemplateId, sectionId: number, data: Partial<TemplateStepResponse>, parentStepId?: number) => {
        return TMTemplateAPI.createTemplateStep(sectionId, data, parentStepId).pipe(
            switchMap(() => this.fetchTMTemplate(templateId)),
            handleMessage({
                type: requestMessage('create_tm_template_section_step'),
            }),
        );
    };

    updateTemplateSectionStep = (templateId: TimeMaterialTemplateId, id: number, data: Partial<TemplateStepResponse>) => {
        return TMTemplateAPI.updateTemplateStep(id, data).pipe(
            switchMap(() => this.fetchTMTemplate(templateId)),
            handleMessage({
                type: requestMessage('update_tm_template_section_step'),
            }),
        );
    };

    deleteTemplateSectionStep = (templateId: TimeMaterialTemplateId, id: number) => {
        return showDeleteConfirmation('Delete Step', 'Do you really want to remove this Step?').pipe(
            switchMap(() => TMTemplateAPI.deleteTemplateStep(id)),
            switchMap(() => this.fetchTMTemplate(templateId)),
            handleMessage({
                type: requestMessage('delete_tm_template_section_step'),
            }),
        );
    };

    sortSectionSteps = (templateId: TimeMaterialTemplateId, sectionId: number, stepIds: number[]) => {
        return TMTemplateAPI.sortSectionSteps(stepIds).pipe(
            tap(() => {
                const template = this.tmTempRepo.getItem(templateId)! as TimeMaterialTemplateResponse;
                const affectedSection = R.find(template.sections, s => s.id === sectionId)!;
                const newSteps = affectedSection.steps.map(step => {
                    return {...step, menuOrder: R.findIndex(stepIds, i => i === step.id)};
                });

                const newSections = template.sections.map(sec => {
                    if (sec.id === sectionId) {
                        return {...sec, steps: R.sortBy(newSteps, s => s.menuOrder)};
                    }
                    return sec;
                });

                this.tmTempRepo.updateOne({
                    id: templateId,
                    changes: () => ({sections: newSections}),
                });
            }),
            handleMessage({
                error: requestMessage('update_tm_template_steps_sort'),
            }),
        );
    };
}
